import 'twin.macro';
import { SingleNote } from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/components/Notes/SingleNote';
import React, { memo } from 'react';

import { Eleve } from '@teetsh/app/src/types/eleves';
import { Note } from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import { useUpdateNoteWithHistory } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/evaluations/useUpdateNote';
import { useUpdateNoteWithAllSkills } from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/hooks/useUpdateNoteWithAllSkills';
import { useCDNContext } from '@teetsh/app/src/containers/CarnetsDeNotes/CarnetDeNotesContext';
import { useEvaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/evaluations/useEvaluation';
import tw from 'twin.macro';

interface Props {
  eleve: Eleve;
  evaluationDate?: Date;
  notes: Note[];
  getInputRef: any;
  getNextInputRef: any;
  evaluationId: string;
}

export function EleveNotes(props: Props) {
  const { eleve, notes, evaluationDate, evaluationId } = props;
  const { cdnId, notationSystem } = useCDNContext();
  const { data: evaluation } = useEvaluation(cdnId, evaluationId);
  const updateNote = useUpdateNoteWithHistory(
    {
      id: cdnId,
      minValue: notationSystem.minValue,
      maxValue: notationSystem.maxValue,
    },
    evaluationId,
  );
  const updateNoteWithAllSkills = useUpdateNoteWithAllSkills(
    evaluationId,
    eleve.id,
    notes,
    evaluation!,
  );

  const isMultiSkill =
    notes.length > 1 ||
    (notes.length === 1 && notes[0].referentialItemId != null);

  if (isMultiSkill) {
    return (
      <div>
        <div tw="mb-2">{`${eleve?.givenName} ${eleve?.familyName}`}</div>
        <div tw="space-y-2 overflow-x-auto">
          {notes.map(note => (
            <div
              key={note.id}
              data-testid={`Line ${eleve.familyName} ${note.referentialItemValue} `}
              tw="flex justify-between flex-col md:flex-row md:items-center flex-wrap overflow-x-auto"
            >
              <div tw="italic text-sm text-muted mr-5 truncate flex-1">
                {note.referentialItemValue}
              </div>
              <SingleNote
                value={note}
                inputRef={props.getInputRef(note.id)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();

                    return props.getNextInputRef(note.id).current.focus();
                  }
                  return null;
                }}
                onChange={async v => {
                  await updateNoteWithAllSkills({
                    ...note,
                    note: v,
                  });
                }}
                aria-label={`SingleNote of ${eleve.givenName} ${eleve.familyName}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (notes.length === 1) {
    return (
      <div tw="flex justify-between flex-col md:flex-row md:items-center ">
        <div
          css={[
            tw`min-w-[6rem]`,
            !eleve.isEnrolledAt?.(evaluationDate) && tw`line-through`,
          ]}
        >{`${eleve?.givenName} ${eleve?.familyName}`}</div>
        <SingleNote
          value={notes[0]}
          inputRef={props.getInputRef(notes[0].id)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (props.getNextInputRef(notes[0].id)) {
                return props.getNextInputRef(notes[0].id).current.focus();
              }
            }
            return null;
          }}
          onChange={async v => {
            await updateNote({
              ...notes[0],
              note: v,
            });
          }}
          aria-label={`StudendNoteSingleRow note of ${eleve.givenName} ${eleve.familyName}`}
        />
      </div>
    );
  }
  return null;
}

function propsAreEquals(prev, next) {
  const isSameEleve = prev.eleve.id === next.eleve.id;
  const isSameNotes = JSON.stringify(prev.notes) === JSON.stringify(next.notes);
  const isSameDate = prev.evaluationDate === next.evaluationDate;
  const isSameEvaluationId = prev.evaluationId === next.evaluationId;
  return isSameEleve && isSameNotes && isSameDate && isSameEvaluationId;
}

export default memo(EleveNotes, propsAreEquals);
